import { apiHeaders } from './api';

/**
 * The church-owned settings behind the widgets' links: where the profile,
 * giving and pay pages live, Online Giving's donor page, the group finder,
 * and which Pledge_Campaigns row See Jesus writes to.
 *
 * Source of truth is MP's dp_Configuration_Settings (application code
 * CUSTOMWIDGETS, the same one the stock custom widgets use), read through
 * apps/api at boot so staff change a link in one place and nothing deploys.
 * An explicit `data-*` attribute or config value on the embed still wins,
 * and these defaults hold until MP answers, or if it cannot.
 */
export interface WidgetSettings {
  profileUrl: string;
  giveUrl: string;
  payUrl: string;
  manageGivingUrl: string;
  groupFinderUrl: string;
  seeJesusCampaignId: number;
}

export const DEFAULT_SETTINGS: WidgetSettings = {
  profileUrl: 'https://woodsidebible.org/profile/',
  giveUrl: 'https://woodsidebible.org/give/',
  payUrl: 'https://woodsidebible.org/pay/',
  manageGivingUrl: 'https://woodsidebible.onlinegiving.org/donate/profile',
  groupFinderUrl: 'https://woodsidebible.org/group-finder/',
  seeJesusCampaignId: 194,
};

/** MP Key_Name for each setting (apps/api sends these names back verbatim). */
const MP_KEYS: Record<keyof WidgetSettings, string> = {
  profileUrl: 'ProfileBaseURL',
  giveUrl: 'GiveBaseURL',
  payUrl: 'PayBaseURL',
  manageGivingUrl: 'ManageGivingURL',
  groupFinderUrl: 'GroupFinderBaseURL',
  seeJesusCampaignId: 'SeeJesusCampaignID',
};

const KEYS = Object.keys(MP_KEYS) as (keyof WidgetSettings)[];

/** What MP has to say, keyed like the widget wants. Throws when the API does not answer. */
export async function fetchWidgetSettings(
  apiBaseUrl: string,
  signal?: AbortSignal
): Promise<Partial<WidgetSettings>> {
  const base = apiBaseUrl.replace(/\/$/, '');
  const res = await fetch(`${base}/api/settings/widgets`, { headers: apiHeaders(), signal });
  if (!res.ok) throw new Error(`settings responded ${res.status}`);
  const body = (await res.json()) as { settings?: Record<string, string> };
  const raw = body.settings ?? {};
  const out: Partial<WidgetSettings> = {};
  for (const key of KEYS) {
    const value = raw[MP_KEYS[key]];
    if (value === undefined) continue;
    if (key === 'seeJesusCampaignId') {
      const n = Number(value);
      if (Number.isInteger(n) && n > 0) out.seeJesusCampaignId = n;
    } else {
      out[key] = value;
    }
  }
  return out;
}

/** Embed overrides first, then MP, then the defaults. */
export function resolveSettings(
  overrides: Partial<WidgetSettings> | undefined,
  fromMp: Partial<WidgetSettings> | null
): WidgetSettings {
  const out: WidgetSettings = { ...DEFAULT_SETTINGS };
  for (const key of KEYS) {
    const value = overrides?.[key] ?? fromMp?.[key];
    if (value !== undefined) (out as unknown as Record<string, unknown>)[key] = value;
  }
  return out;
}

/** Drop the undefined entries so an absent attribute does not shadow MP. */
export function definedSettings(o: {
  [K in keyof WidgetSettings]?: WidgetSettings[K] | undefined;
}): Partial<WidgetSettings> {
  return Object.fromEntries(
    Object.entries(o).filter(([, v]) => v !== undefined)
  ) as Partial<WidgetSettings>;
}
